import { CheckCircle, FileText, Clock, Ban, Calendar } from 'lucide-react';

/**
 * Standard tab configuration for all listing pages
 * Based on listing status
 */
export const getListingTabs = (listings) => {
  return [
    {
      id: 'active',
      label: 'Active',
      icon: CheckCircle,
      statuses: ['active', 'live', 'published'],
      description: 'Listings currently generating platform revenue',
      count: listings.filter(l => ['active', 'live', 'published'].includes(l.status?.toLowerCase())).length
    },
    {
      id: 'draft',
      label: 'Draft',
      icon: FileText,
      statuses: ['draft', 'incomplete'],
      description: 'Listings saved but not submitted for review',
      count: listings.filter(l => ['draft', 'incomplete'].includes(l.status?.toLowerCase())).length
    },
    {
      id: 'pending',
      label: 'Pending Approval',
      icon: Clock,
      statuses: ['pending_approval', 'pending_revision_approval'],
      description: 'Listings awaiting admin review',
      count: listings.filter(l => 
        l.status === 'pending_approval' || 
        l.revision_status === 'pending_revision_approval'
      ).length
    },
    {
      id: 'suspended',
      label: 'Suspended',
      icon: Ban,
      statuses: ['suspended', 'hidden'],
      description: 'Listings temporarily taken down',
      count: listings.filter(l => ['suspended', 'hidden'].includes(l.status?.toLowerCase())).length
    },
    {
      id: 'expired',
      label: 'Expired',
      icon: Calendar,
      statuses: ['expired'],
      description: 'Listings that have passed their time limit',
      count: listings.filter(l => l.status === 'expired').length
    }
  ];
};

/**
 * Filter listings by tab
 */
export const filterListingsByTab = (listings, activeTab) => {
  if (activeTab === 'all') return listings;
  
  const tabs = getListingTabs(listings);
  const tab = tabs.find(t => t.id === activeTab);
  
  if (!tab) return listings;
  
  return listings.filter(listing => {
    // Check main status
    if (tab.statuses.includes(listing.status?.toLowerCase())) return true;
    
    // Check revision status for pending tab
    if (activeTab === 'pending' && listing.revision_status === 'pending_revision_approval') return true;
    
    return false;
  });
};

/**
 * Get standard sorting options for listings
 */
export const getSortingOptions = () => {
  return [
    { value: 'updated_desc', label: 'Recently Updated' },
    { value: 'created_desc', label: 'Newest First' },
    { value: 'rating_desc', label: 'Highest Rated' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'views_desc', label: 'Most Popular' },
    { value: 'title', label: 'Title (A-Z)' }
  ];
};

/**
 * Sort listings based on selected option
 */
export const sortListings = (listings, sortBy) => {
  const sorted = [...listings];
  
  switch (sortBy) {
    case 'updated_desc':
      return sorted.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    case 'created_desc':
      return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    case 'rating_desc':
      return sorted.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
    case 'price_asc':
      return sorted.sort((a, b) => {
        const priceA = a.data?.price || a.price || 0;
        const priceB = b.data?.price || b.price || 0;
        return priceA - priceB;
      });
    case 'price_desc':
      return sorted.sort((a, b) => {
        const priceA = a.data?.price || a.price || 0;
        const priceB = b.data?.price || b.price || 0;
        return priceB - priceA;
      });
    case 'views_desc':
      return sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
    case 'title':
      return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    default:
      return sorted;
  }
};
